/* eslint-disable no-param-reassign */
import { EditorState, QueryMethods, ROOT_NODE } from '@candulabs/craft';
import invariant from 'invariant';
import flatten from 'lodash/flatten';

import { resolvers } from 'craft/resolvers';
import { COLLECTIONITEM_DEFAULT_STYLES } from 'craft/resolvers/CollectionItem';
import { CraftNode, Resolver } from 'craft/types';

const shouldHaveAsParent = [resolvers.Collection];
const collectionChildrenResolver = [resolvers.CollectionItem];

export const normalizeCollection = (editorState: EditorState) => {
  // Break the Slate node if there's non-Slate elements in it
  wrapStandAloneElement(editorState);

  normalizeCollectionItems(editorState);

  trimCollections(editorState);

  return editorState;
};

export const wrapStandAloneElement = (editorState: EditorState) => {
  const standaloneElements = Object.values(editorState.nodes).filter((node) => {
    // the node is not a collection children resolver return true;
    return (
      !!editorState.nodes[node.data.parent] &&
      !collectionChildrenResolver.includes(node.data.type as Resolver) &&
      shouldHaveAsParent.includes(editorState.nodes[node.data.parent].data.type as Resolver)
    );
  });

  standaloneElements.forEach((node) => {
    const newCollectionItemNode = QueryMethods(editorState)
      .parseFreshNode({
        data: {
          type: resolvers.CollectionItem,
          nodes: [node.id],
          parent: node.data.parent,
          custom: {
            css: COLLECTIONITEM_DEFAULT_STYLES,
          },
        },
      })
      .toNode();

    const currentParentNode = editorState.nodes[node.data.parent];
    const indexInParentNode = currentParentNode.data.nodes.indexOf(node.id);

    if (indexInParentNode > -1) {
      currentParentNode.data.nodes.splice(indexInParentNode, 1, newCollectionItemNode.id);
    }

    // eslint-disable-next-line no-param-reassign
    node.data.parent = newCollectionItemNode.id;
    // eslint-disable-next-line no-param-reassign
    editorState.nodes[newCollectionItemNode.id] = newCollectionItemNode;
  });

  return editorState;
};

const normalizeCollectionItems = (editorState: EditorState) => {
  const standaloneElements = Object.values(editorState.nodes).filter((node) => {
    return shouldHaveAsParent.includes(node.data.type as Resolver);
  });
  standaloneElements.forEach((node: CraftNode) => {
    /**
     * First, let's trim all the unnecessary nodes.
     * this first block is made to tranfrom
     * Input:
     *   <Collection>
     *     <CollectionItem />
     *   </Collection>
     *
     * Output:
     *   <Collection />
     *
     * as long as they have no children.
     */
    const collectionItemsToDelete = node.data.nodes.filter((collectionItemId) => {
      const collectionItem = editorState.nodes[collectionItemId];
      // at this point, we already know all teh items are wrapped, but we might as
      // well do a sanity check
      invariant(
        collectionItem.data.type === resolvers.CollectionItem,
        'Collections can only have Collection Items as children',
      );
      return collectionItem.data.nodes.length === 0;
    });

    collectionItemsToDelete.forEach((nodeId) => {
      delete editorState.nodes[nodeId];
    });
    node.data.nodes = [...node.data.nodes].filter((nid) => !collectionItemsToDelete.includes(nid));

    /*
     * Second, let's get rid of all the unnecessary CollectionItem blocks.
     * This is made to transform cases like
     * Input:
     *   <Collection>
     *     <CollectionItem>
     *       <View />
     *     <CollectionItem>
     *     <CollectionItem>
     *       <View />
     *     <CollectionItem>
     *   </Collection>
     *
     * Output:
     *   <Collection>
     *     <CollectionItem>
     *       <View />
     *       <View />
     *     <CollectionItem>
     *   </Collection>
     */
    const numberOfChildren = node.data.nodes.length;
    // we have the right number of children, we can return
    if (numberOfChildren < 2) {
      return;
    }
    const [firstChildrenId, ...allTheOtherChildrens] = node.data.nodes;
    // assign the parent to have the correct children
    node.data.nodes = [firstChildrenId];

    // take all the other descendants
    const allOtherChildrenDescendants = flatten(
      allTheOtherChildrens.map((otherChildrenId) => {
        // correctly map the parent Id to the first children
        editorState.nodes[otherChildrenId].data.parent = firstChildrenId;
        return editorState.nodes[otherChildrenId].data.nodes;
      }),
    );
    // merge all the nodes into a single one
    editorState.nodes[firstChildrenId].data.nodes = [
      ...editorState.nodes[firstChildrenId].data.nodes,
      ...allOtherChildrenDescendants,
    ];
  });
};

const trimCollections = (editorState: EditorState) =>
  traverseTrimCollections(editorState, ROOT_NODE);

const trimExcessNode = (editorState: EditorState, node: CraftNode) => {
  // if the node has collections, remove them since it's not part of a collection
  if (node.data.custom.collections) {
    editorState.nodes[node.id].data.custom.collections = null;
  }
  if (node.data.type !== resolvers.CollectionItem) {
    return;
    // we're done with normalization now
  }
  node.data.type = resolvers.View;
  node.data.displayName = 'Layout';
  node.data.name = 'View';
};

const traverseTrimCollections = (editorState: EditorState, nodeId: string) => {
  const node = editorState.nodes[nodeId];
  // if there is not a note or the node is a collection, then just skip.
  if (!node || node.data.type === resolvers.Collection) {
    return;
  }
  trimExcessNode(editorState, node as CraftNode);

  if (!node.data.nodes) {
    return;
  }
  // traverse all the children
  node.data.nodes.forEach((nid) => traverseTrimCollections(editorState, nid));
};
